import {RouteLocationRaw, RouteParams} from 'vue-router'
import {MessageKey} from '@/i18n'
import {LEVEL_COLOR} from '@/types/AccessLevel'
import {shade} from '@/utils/colors'

/**
 * Para onde a casca leva, e a cor dela.
 *
 * **Já houve três modos aqui** — usar, desenhar e dar acesso —, cada um com a sua cor de barra, o
 * seu selo e o seu botão de troca. Saíram (pedido do usuário): o app tem uma estética só, a do uso;
 * o design de cada coisa é um degrau do caminho ("Ambiente › DESIGN", "Coleção › DESIGN"), e os
 * acessos são uma aba do design do ambiente. O que sobrou é isto.
 */

/**
 * A cor da barra, a mesma em toda tela: a que era a do uso — o índigo do editor, escurecido.
 *
 * Do `LEVEL_COLOR`, e não escrita de novo aqui: duas listas concordando por coincidência, a primeira
 * a mudar deixaria a outra mentindo.
 */
export const BAR_COLOR = shade(LEVEL_COLOR.editor, 'darken-4')

/**
 * O ícone da lista de ambientes, num lugar só.
 *
 * **Um destino, um ícone**: para lá vão a casinha da barra e o último item do seletor de ambiente,
 * e com dois ícones diferentes os dois caminhos leem como dois lugares. Na barra do telefone, onde
 * os destinos viram menu, eles ficam um ao lado do outro — e a diferença, que antes passava, vira
 * duas listas quase gêmeas dizendo coisas diferentes.
 */
export const HOME_ICON = 'mdi-home-group'

/**
 * O ícone da home do ambiente em que se está.
 *
 * A casinha simples ao lado da casinha-grupo: uma é este ambiente, a outra é a lista deles. A
 * semelhança é de propósito — são o mesmo tipo de lugar em duas escalas —, e o que as separa é o
 * grupo, que é exatamente a diferença que elas têm de dizer.
 */
export const WORKSPACE_ICON = 'mdi-home'

/** Um lugar para onde se pode ir daqui, na forma que o botão e o item de menu precisam. */
export type Destination = {
    key: string
    icon: string
    /**
     * A frase que diz o destino SOZINHA — "Início do ambiente", "Todos os ambientes".
     *
     * Um texto só para as duas formas: é o tooltip do botão de ícone e é o rótulo do item de menu.
     */
    label: MessageKey
    to: RouteLocationRaw
}

/**
 * Para onde se pode ir daqui: a home do ambiente em que se está, e a lista de ambientes no fim.
 *
 * A engrenagem do design e o botão dos acessos saíram (pedido do usuário): o design se alcança pelo
 * menu "Configurar" e pela faixa do caminho, e os acessos são uma aba dele.
 *
 * Mora aqui, e não no componente que a desenha, pela razão que o `breadcrumb` já escreve: regra de
 * navegação é função pura, e dentro do componente ela viraria `v-if` no template.
 *
 * **Sem ambiente, só a lista de ambientes.** E é por isso que o parâmetro é o ambiente: fora de um
 * ambiente o `App.vue` continua com o workspace do ANTERIOR em mão, e a casinha apontaria para o
 * ambiente que a pessoa acabou de deixar. Quem chama passa `null` quando não há ambiente de verdade.
 */
export function destinationsFor(workspace: object | null | undefined, params: RouteParams): Destination[] {
    return [
        //Só o `system` nos parâmetros: a home do ambiente não tem coleção nem item, e carregar
        //os de onde se estava deixaria na URL o que a tela não usa
        ...(workspace ? [{
            key: 'workspace',
            icon: WORKSPACE_ICON,
            label: 'workspace-home' as MessageKey,
            to: {name: 'view-system', params: {system: params.system}}
        }] : []),
        {key: 'home', icon: HOME_ICON, label: 'workspaces-all' as MessageKey, to: {name: 'home'}}
    ]
}
